import React from 'react';
import { motion } from 'framer-motion';
import { Compass, PenTool, Cog, Sparkles, ArrowRight } from 'lucide-react';

const STEPS = [
    { id: 'discover', num: '01', icon: Compass, title: 'Discover', desc: 'Understanding your vision, audience, and objectives.' },
    { id: 'design', num: '02', icon: PenTool, title: 'Design', desc: 'Creating concepts, themes, and experience strategies.' },
    { id: 'produce', num: '03', icon: Cog, title: 'Produce', desc: 'Managing production, technology, entertainment, and execution.' },
    { id: 'deliver', num: '04', icon: Sparkles, title: 'Deliver', desc: 'Creating seamless experiences that leave lasting impact.' },
];

export const Process = () => {
    return (
        <section id="process" data-testid="process-root" className="relative py-24 sm:py-32 bg-neutral-950 border-y border-white/5 overflow-hidden">
            <div className="container-lux">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 items-end">
                    <div className="lg:col-span-7">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-8 h-px bg-gold" />
                            <span className="eyebrow">Our Process</span>
                            <span className="text-white/30 text-[10px] tracking-[0.3em]">03</span>
                        </div>
                        <h2 className="font-display font-light text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                            From concept to
                            <br />
                            curtain <span className="italic text-gold font-extralight">call.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-4 lg:col-start-9">
                        <p className="text-white/60 text-base leading-relaxed font-light">
                            A four-step approach that turns ambition into a flawlessly executed experience — every project, every time.
                        </p>
                    </div>
                </div>

                {/* Desktop timeline */}
                <div className="hidden md:block relative">
                    <div className="absolute top-[52px] left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/50 to-transparent" />
                    <div className="grid grid-cols-4 gap-6">
                        {STEPS.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <motion.div
                                    key={s.id}
                                    data-testid={`process-step-${s.id}`}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: '-60px' }}
                                    transition={{ duration: 0.6, delay: i * 0.12 }}
                                    className="relative group"
                                >
                                    <div className="flex justify-center mb-6">
                                        <div className="relative w-[104px] h-[104px] rounded-full border border-gold/40 bg-black flex items-center justify-center group-hover:border-gold transition-colors">
                                            <Icon size={28} strokeWidth={1.2} className="text-gold group-hover:scale-110 transition-transform" />
                                            <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-gold text-black font-display text-xs font-bold flex items-center justify-center">
                                                {s.num}
                                            </div>
                                        </div>
                                    </div>
                                    <h4 className="font-display text-white text-xl font-medium tracking-tight text-center mb-3">{s.title}</h4>
                                    <p className="text-white/60 text-sm leading-relaxed font-light text-center">{s.desc}</p>
                                    {i < STEPS.length - 1 && (
                                        <ArrowRight size={16} strokeWidth={1.4} className="hidden lg:block absolute top-[45px] -right-3 text-gold/70" />
                                    )}
                                </motion.div>
                            );
                        })}
                    </div>
                </div>

                {/* Mobile vertical timeline */}
                <div className="md:hidden space-y-8 relative">
                    <div className="absolute left-[35px] top-8 bottom-8 w-px bg-gradient-to-b from-transparent via-gold/40 to-transparent" />
                    {STEPS.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <motion.div
                                key={s.id}
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.1 }}
                                className="relative flex gap-6 items-start"
                            >
                                <div className="relative w-[70px] h-[70px] rounded-full border border-gold/40 bg-black flex items-center justify-center flex-shrink-0">
                                    <Icon size={22} strokeWidth={1.3} className="text-gold" />
                                    <div className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-gold text-black font-display text-[10px] font-bold flex items-center justify-center">
                                        {s.num}
                                    </div>
                                </div>
                                <div className="flex-1 pt-1">
                                    <h4 className="font-display text-white text-lg font-medium mb-2">{s.title}</h4>
                                    <p className="text-white/60 text-sm leading-relaxed font-light">{s.desc}</p>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default Process;
